import { useCallback, useMemo, useState } from "react";
import { Segmented } from "@/common/Segmented";
import { CheckIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import Button from "@/common/Button";
import Link from "@/common/Link";
import Panel from "@/common/Panel/Panel.tsx";
import {
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "@/catalyst/dropdown.tsx";
import {
    useGetSelfServePlansQuery,
    useStartSubscriptionCheckoutSessionMutation,
} from "@/services/apis/api.ts";
import type {
    BillingCurrency,
    Cadence,
} from "@/types/generated/management-api-types";
import type { paths } from "@/types/generated/api-schema";
import {
    formatAmount,
    perMonthMinorUnits,
    yearlySavingMinorUnits,
} from "./pricing";

/**
 * The two plans this screen can actually sell. Taken from the checkout
 * request body rather than the Plan enum, which also carries `manager`,
 * `free`, `beta`, `utm_scout` and `enterprise` — none of which a customer
 * can buy here.
 */
type SelfServePlan =
    paths["/user/organization/subscription/checkout-session"]["post"]["requestBody"]["content"]["application/json"]["plan"];

const CURRENCIES: BillingCurrency[] = ["eur", "usd"];
const CADENCES: Cadence[] = ["monthly", "yearly"];

const PLAN_COPY: Record<SelfServePlan, { name: string; blurb: string }> = {
    essential: {
        name: "Essential",
        blurb: "Attribution for a single team, with everything needed to see which channels earn revenue.",
    },
    scale: {
        name: "Scale",
        blurb: "For teams running several channels at once, with the deeper reporting to compare them.",
    },
};

const CADENCE_LABEL: Record<Cadence, string> = {
    monthly: "Monthly",
    yearly: "Yearly",
};

// Iso 4217 codes are conventionally upper case ("EUR", not "Eur"), so the
// house rule for abbreviations does not apply to them.
const currencyCode = (currency: BillingCurrency) => currency.toUpperCase();

// Prices arrive as minor units, the way Stripe stores them. Whole amounts
// lose the ".00" — every price in the book is a whole unit today, and
/**
 * "Billed in EUR" with a chevron that opens the other currencies. Kept
 * quieter than the cadence toggle on purpose: almost everyone wants the
 * currency they were shown, and a second segmented control would give the
 * choice the same weight as monthly-versus-yearly.
 */
const CurrencyMenu = ({
    value,
    onChange,
}: {
    value: BillingCurrency;
    onChange: (next: BillingCurrency) => void;
}) => (
    <Dropdown>
        <DropdownButton
            as="button"
            type="button"
            aria-label="Billing currency"
            className="inline-flex items-center gap-1 rounded-md text-sm text-zinc-500 hover:text-zinc-950 focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-pink-500 dark:text-zinc-400 dark:hover:text-white"
        >
            Billed in {currencyCode(value)}
            <ChevronDownIcon aria-hidden="true" className="size-4" />
        </DropdownButton>
        <DropdownMenu anchor="bottom start">
            {CURRENCIES.map((currency) => (
                <DropdownItem key={currency} onClick={() => onChange(currency)}>
                    {currency === value && <CheckIcon />}
                    <DropdownLabel>{currencyCode(currency)}</DropdownLabel>
                </DropdownItem>
            ))}
        </DropdownMenu>
    </Dropdown>
);

export default function PlanPicker({
    lockedCurrency,
}: {
    /**
     * Set once Stripe has committed this customer to a currency for
     * recurring billing. The menu is replaced by a plain label then:
     * Checkout would reject any other currency, so offering the choice
     * would only produce a dead end.
     */
    lockedCurrency?: BillingCurrency;
}) {
    const { data: plans, isLoading, isError } = useGetSelfServePlansQuery();
    const [startCheckout, { isLoading: isStartingCheckout }] =
        useStartSubscriptionCheckoutSessionMutation();

    const [currency, setCurrency] = useState<BillingCurrency>(
        lockedCurrency ?? "eur",
    );
    const [cadence, setCadence] = useState<Cadence>("monthly");
    const [pendingPlan, setPendingPlan] = useState<SelfServePlan>();

    const visible = useMemo(
        () => (plans ?? []).filter((price) => price.cadence === cadence),
        [plans, cadence],
    );

    // The monthly price of each plan, kept regardless of which cadence is
    // showing: it is what the yearly saving is measured against, and the
    // endpoint sends both cadences in one payload, so there is nothing to
    // fetch.
    const monthlyByPlan = useMemo(
        () =>
            new Map(
                (plans ?? [])
                    .filter((price) => price.cadence === "monthly")
                    .map((price) => [price.plan, price.amounts]),
            ),
        [plans],
    );

    const handleChoose = useCallback(
        async (plan: SelfServePlan) => {
            setPendingPlan(plan);

            try {
                const session = await startCheckout({
                    plan,
                    cadence,
                    currency,
                }).unwrap();

                // A full navigation, not a router push: Checkout is hosted
                // by Stripe and lives outside this app.
                window.location.assign(session.url);
            } finally {
                setPendingPlan(undefined);
            }
        },
        [cadence, currency, startCheckout],
    );

    if (isLoading) {
        return <p className="text-semi-subtle">Loading plans…</p>;
    }

    if (isError || visible.length === 0) {
        return (
            <p className="text-semi-subtle">
                We could not load our plans just now. Please{" "}
                <Link to="mailto:support@spectaclehq.com" external={true}>
                    Contact support
                </Link>{" "}
                and we will get you set up.
            </p>
        );
    }

    return (
        <div className="flex w-full flex-col items-center gap-8">
            <div className="flex flex-wrap items-center justify-center gap-4">
                <Segmented
                    label="Billing cadence"
                    options={CADENCES}
                    value={cadence}
                    onChange={setCadence}
                    renderOption={(option) => CADENCE_LABEL[option]}
                />
                {lockedCurrency ? (
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">
                        Billed in {currencyCode(lockedCurrency)}
                    </span>
                ) : (
                    <CurrencyMenu value={currency} onChange={setCurrency} />
                )}
            </div>

            <div className="grid w-full max-w-3xl gap-6 sm:grid-cols-2">
                {visible.map((price) => {
                    const amount = price.amounts[currency];
                    const copy = PLAN_COPY[price.plan];

                    // Always a per-month figure, on both cadences. The
                    // amount Stripe will actually charge moves to the line
                    // underneath on yearly, where it is the fact the
                    // customer needs but not the one they are comparing.
                    const perMonth =
                        amount === undefined
                            ? undefined
                            : perMonthMinorUnits(amount, cadence, currency);
                    const saving =
                        cadence === "yearly"
                            ? yearlySavingMinorUnits(
                                  monthlyByPlan.get(price.plan)?.[currency],
                                  amount,
                              )
                            : undefined;

                    return (
                        <Panel
                            key={price.plan}
                            className="flex flex-col text-left"
                        >
                            <h3 className="text-lg font-semibold tracking-tight text-default">
                                {copy.name}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                                {copy.blurb}
                            </p>

                            <p className="mt-6 flex items-baseline gap-x-1">
                                <span className="text-4xl font-semibold tracking-tight text-default tabular-nums">
                                    {perMonth === undefined
                                        ? "—"
                                        : formatAmount(perMonth, currency)}
                                </span>
                                <span className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">
                                    /month
                                </span>
                            </p>

                            {/* Reserved on both cadences so the two cards
                                and the two tabs keep the same height —
                                without it, switching cadence nudges the
                                buttons down the page. */}
                            <p className="mt-1 min-h-5 text-sm text-zinc-500 dark:text-zinc-400">
                                {cadence === "yearly" &&
                                    amount !== undefined && (
                                        <>
                                            {formatAmount(amount, currency)}{" "}
                                            billed yearly
                                            {saving !== undefined && (
                                                <>
                                                    {" · save "}
                                                    {formatAmount(
                                                        saving,
                                                        currency,
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                            </p>

                            <ul
                                role="list"
                                className="mt-6 space-y-3 text-sm text-semi-subtle"
                            >
                                <li className="flex gap-x-3">
                                    <CheckIcon
                                        aria-hidden="true"
                                        className="h-6 w-5 flex-none text-indigo-500 dark:text-indigo-400"
                                    />
                                    1,000,000 tracked events per month
                                </li>
                                {/* The yearly saving is stated in money on
                                    the line under the price, not as a count
                                    of free months. It used to claim "two
                                    months off" and had been wrong for a
                                    year — the prices said about 1.3 — so it
                                    is derived from the monthly price in the
                                    same payload now and cannot drift from
                                    what Stripe will charge. */}
                            </ul>

                            <Button
                                type="primary"
                                className="mt-8"
                                loading={
                                    isStartingCheckout &&
                                    pendingPlan === price.plan
                                }
                                disabled={
                                    isStartingCheckout || amount === undefined
                                }
                                onClick={() => handleChoose(price.plan)}
                            >
                                Choose {copy.name}
                            </Button>
                        </Panel>
                    );
                })}
            </div>

            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Need more than this?{" "}
                <Link
                    to={`https://${import.meta.env.VITE_DOMAIN}/contact`}
                    external={true}
                >
                    Talk to us about Enterprise
                </Link>
            </p>
        </div>
    );
}
